import { create } from 'zustand';

/**
 * Identity of one registered stream, compared by reference and nothing else.
 * It is an empty frozen object: it carries no state and controls nothing.
 */
type StreamToken = object;

/**
 * Cross-thread stream tracking.
 *
 * Story 22.1: Slimmed down from the previous 158-line version. The new
 * `useDataStreamRuntime` manages per-thread streaming state internally, so
 * this store only keeps the features that need a cross-thread view:
 *
 * 1. **Sidebar streaming indicators** — `chat-history-list.tsx` reads
 *    `getStreamingSessionIds()` to show a spinner next to currently
 *    streaming chats (including those in inactive threads).
 * 2. **Survives a workspace switch** — a run keeps streaming into its thread
 *    while the tab shows another workspace, so `resetAllStores` leaves this
 *    store alone (KAN-1471). Only leaving the user clears it
 *    (`resetUserStores`, on logout and when the session is gone).
 * 3. **Stall watchdog liveness** — `recordActivity()` stamps the last time a
 *    byte (token OR the server's 15s keep-alive heartbeat) arrived on a
 *    session's stream. The runtime's watchdog reads `getLastActivityAt()` to
 *    detect a silently wedged SSE connection (proxy/network cut with no error
 *    event) and trigger recovery. Heartbeats reset the stamp every 15s, so a
 *    long tool call never looks stalled while the connection is alive.
 *
 * 4. **Did the answer actually end?** — `recordAnswerComplete()` is set when
 *    the response carried the protocol's terminal frame. A stream that ends
 *    without it did not end on purpose (dropped connection, a mobile WebView
 *    thawed into a dead socket), and the runtime then goes looking for the
 *    rest instead of presenting a truncated answer as finished (KAN-896).
 * 5. **Is anything streaming in this tab?** — `activeStreams.size` holds back
 *    the post-deploy reload (`use-version-check.ts`) and the chunk-error
 *    reload (`route-error.tsx`), so neither cuts off an answer on screen.
 * 6. **Is the run being got back?** — `recovering` holds a session from the
 *    moment `recoverRun` takes it on until it has re-attached or taken what the
 *    database has. The thread shows no run meanwhile, but the answer is not
 *    over; the voice conversation goes on reading it (KAN-1490). `breaks`
 *    counts every break up, also one a recovery already under way takes on:
 *    the voice conversation tells by it which answer a break cut off.
 *
 * The store tracks runs, it does not own them: no request hangs off a
 * registered stream, so nothing here can cancel one. Stopping an answer is the
 * runtime's job (the composer's cancel), and `reset()` on logout only forgets.
 *
 * Error tracking was removed in Story 22.1: limit errors now live in
 * `useBudgetStore.limitAlert`, and general errors are handled directly in
 * the runtime's `onError` callback via `setComposerError` on the thread.
 */
interface StreamingState {
  /** The current stream's token per streaming session. */
  activeStreams: Map<string, StreamToken>;
  /** Last time (ms epoch) a byte arrived on a session's stream. */
  lastActivityAt: Map<string, number>;
  /** Sessions whose current stream carried the server's terminal frame. */
  answerComplete: Set<string>;
  /** Sessions a recovery is getting back on its feet right now, and how many (`recoverRun`). */
  recovering: Map<string, number>;
  /** How often each session's connection broke so far; only ever counts up. */
  breaks: Map<string, number>;

  /**
   * Register a new stream for a session and return its token. A stream
   * already registered for the same session is replaced: its token stops
   * counting for `recordAnswerComplete`, but its request is not touched (the
   * store never had hold of it).
   */
  startStream: (sessionId: string) => StreamToken;

  /**
   * Mark a stream as finished (normal completion, error, or cancellation).
   * Idempotent — safe to call multiple times for the same session.
   */
  endStream: (sessionId: string) => void;

  /**
   * Record stream liveness for a session — call on every received chunk
   * (token or heartbeat). No-op for a session that is not currently
   * streaming, so a late chunk after `endStream` can't resurrect a stamp.
   */
  recordActivity: (sessionId: string) => void;

  /**
   * Note that the server closed this session's answer on purpose — it sent
   * the terminal frame. Read it back with `answerEndedOnPurpose` BEFORE
   * `endStream`, which clears it along with the rest of the stream's state.
   *
   * `token` identifies the stream the frame arrived on: the one `startStream`
   * returned, or `getStreamToken` read right after it. A frame from an earlier
   * run that only now drains — the user stopped an answer and immediately asked
   * again — would otherwise mark the NEW run as finished, which is exactly
   * the wrong answer for the one case this whole mark exists for.
   */
  recordAnswerComplete: (sessionId: string, token: StreamToken) => void;

  /**
   * The session's connection broke and a recovery takes it on: the one it
   * starts, or the one already under way. Not touched by `endStream`, which it
   * begins with.
   */
  beginRecovery: (sessionId: string) => void;
  /** A recovery is through: the run is attached again, or the database has been read. */
  endRecovery: (sessionId: string) => void;

  // Selectors
  isStreaming: (sessionId: string) => boolean;
  getStreamingSessionIds: () => string[];
  /** Last-activity timestamp (ms epoch) for a session, or undefined. */
  getLastActivityAt: (sessionId: string) => number | undefined;
  /** Identity of the session's current stream, for `recordAnswerComplete`. */
  getStreamToken: (sessionId: string) => StreamToken | undefined;
  /** True when the server declared this session's answer finished. */
  answerEndedOnPurpose: (sessionId: string) => boolean;

  /**
   * Reset the entire store — on logout / auth bounce, and in test setups.
   */
  reset: () => void;
}

/** `counts` with the count of `sessionId` one more or less; none left drops it. */
function counted(counts: Map<string, number>, sessionId: string, by: 1 | -1): Map<string, number> {
  const next = new Map(counts);
  const left = (next.get(sessionId) ?? 0) + by;
  if (left > 0) next.set(sessionId, left);
  else next.delete(sessionId);
  return next;
}

export const useStreamingStore = create<StreamingState>()((set, get) => ({
  activeStreams: new Map<string, StreamToken>(),
  lastActivityAt: new Map<string, number>(),
  answerComplete: new Set<string>(),
  recovering: new Map<string, number>(),
  breaks: new Map<string, number>(),

  startStream: (sessionId: string) => {
    // A fresh identity per run, so a late frame still carrying the token of
    // the stream this one replaces is ignored by `recordAnswerComplete`.
    const token: StreamToken = Object.freeze({});
    set((state) => {
      const next = new Map(state.activeStreams);
      next.set(sessionId, token);
      const nextActivity = new Map(state.lastActivityAt);
      // Seed liveness at start so the watchdog measures from run start, not
      // from the first token (which may legitimately take >15s).
      nextActivity.set(sessionId, Date.now());
      // A fresh run has not ended yet, whatever the last one did.
      const nextComplete = new Set(state.answerComplete);
      nextComplete.delete(sessionId);
      return { activeStreams: next, lastActivityAt: nextActivity, answerComplete: nextComplete };
    });
    return token;
  },

  endStream: (sessionId: string) => {
    set((state) => {
      if (
        !state.activeStreams.has(sessionId) &&
        !state.lastActivityAt.has(sessionId) &&
        !state.answerComplete.has(sessionId)
      ) {
        return state;
      }
      const next = new Map(state.activeStreams);
      next.delete(sessionId);
      const nextActivity = new Map(state.lastActivityAt);
      nextActivity.delete(sessionId);
      const nextComplete = new Set(state.answerComplete);
      nextComplete.delete(sessionId);
      return { activeStreams: next, lastActivityAt: nextActivity, answerComplete: nextComplete };
    });
  },

  recordActivity: (sessionId: string) => {
    // Ignore a stray chunk for a session that already ended — otherwise a
    // late byte after `endStream` would re-seed a stamp with no stream behind
    // it, which the watchdog can never clear.
    if (!get().activeStreams.has(sessionId)) return;
    set((state) => {
      const nextActivity = new Map(state.lastActivityAt);
      nextActivity.set(sessionId, Date.now());
      return { lastActivityAt: nextActivity };
    });
  },

  recordAnswerComplete: (sessionId: string, token: StreamToken) => {
    // Not this session's current stream any more — a late frame from a run
    // that has already been replaced says nothing about the new one.
    if (get().activeStreams.get(sessionId) !== token) return;
    set((state) => {
      if (state.answerComplete.has(sessionId)) return state;
      const next = new Set(state.answerComplete);
      next.add(sessionId);
      return { answerComplete: next };
    });
  },

  beginRecovery: (sessionId: string) =>
    set((state) => ({
      recovering: counted(state.recovering, sessionId, 1),
      breaks: counted(state.breaks, sessionId, 1),
    })),

  endRecovery: (sessionId: string) =>
    set((state) => ({ recovering: counted(state.recovering, sessionId, -1) })),

  isStreaming: (sessionId: string) => get().activeStreams.has(sessionId),

  getStreamingSessionIds: () => Array.from(get().activeStreams.keys()),

  getLastActivityAt: (sessionId: string) => get().lastActivityAt.get(sessionId),

  getStreamToken: (sessionId: string) => get().activeStreams.get(sessionId),

  answerEndedOnPurpose: (sessionId: string) => get().answerComplete.has(sessionId),

  reset: () => {
    set({
      activeStreams: new Map(),
      lastActivityAt: new Map(),
      answerComplete: new Set(),
      recovering: new Map(),
      breaks: new Map(),
    });
  },
}));
